import { Component, Input, OnInit, ViewEncapsulation } from "@angular/core";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { CurrentUserChangedData, LinkUserService, Organization, PatientLogin, UserLogin, UserLogins, UserService } from "@main/users";
import { MenuItem } from "primeng/api";
import { UltimaComponent } from ".";
import { AppComponent } from "..";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { AccountService } from "@account/services";
import { NhsLoginService } from "../../../../account/services/nhs-login.service";
import { AppStorage } from "../../../../bbwt/utils/app-storage";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { PassportOrganizationId } from "@account/services/passport";

@Component({
    selector: "ultima-user-switch",
    templateUrl: "./user-switch.component.html",
    styleUrls: ["./user-switch.component.scss"],
    encapsulation: ViewEncapsulation.None
})
export class UserSwitchComponent implements OnInit {
    private organizationsMenuItem: MenuItem;

    currentOrganization: { id?: string; name?: string } = {};
    menuItems: MenuItem[];

    isOnline: boolean;
    private _currentUser = AppStorage.getItem<PatientLogin>("current-user");
    private hasNhsConnected: boolean = false;

    @Input() appendTo: any;

    constructor(
        private userService: UserService,
        private linkedUserService: LinkUserService,
        private accountService: AccountService,
        broadcastService: BroadcastService,
        public app: AppComponent,
        public ultimaMain: UltimaComponent,
        public appOnlineStateService: AppOnlineStateService,
        public namingConvService: NamingConventionService,
        private nhsLoginService: NhsLoginService
    ) {
        broadcastService
            .on<CurrentUserChangedData>(UserService.CurrentUserChangedEventName)
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.initMenuItems());
        broadcastService
            .on(LinkUserService.UserOrganizationDelinkedEventName)
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.initMenuItems());
        broadcastService
            .on<{ isAppOnline: boolean }>(AppOnlineStateService.AppOnlineStateChanged)
            .pipe(takeUntilDestroyed())
            .subscribe(data => this.connectionChanged(data));
    }

    ngOnInit() {
        this.hasNhsConnected = !!this._currentUser?.nhsNumber;
        this.isOnline = this.appOnlineStateService.isAppOnline;

        this.initMenuItems();
    }

    connectionChanged(data: { isAppOnline: boolean }) {
        this.isOnline = data.isAppOnline;
        this.initMenuItems();
    }

    private async initMenuItems() {
        this.organizationsMenuItem = {
            label: this.namingConvService.organisationPlural,
            items: []
        };

        this.menuItems = [
            {
                label: "Account",
                items: [
                    {
                        label: "Profile",
                        icon: "mi mi-orb-user-no-col",
                        routerLink: "/app/profile",
                        visible: this.isOnline
                    },
                    {
                        label: "NHS Account Settings",
                        icon: "pi pi-user",
                        visible: !!AppStorage.getItem<boolean>(AppStorage.NhsLoginKey) || this.hasNhsConnected,
                        command: () => this.nhsLoginService.redirectToLoginSettings()
                    },
                    {
                        label: "End Impersonation",
                        icon: "mi mi-orb-power",
                        command: () => this.app.stopImpersonation(),
                        visible: this.app.isUserImpersonate
                    },
                    {
                        label: "Logout",
                        icon: "mi mi-orb-power",
                        command: () => this.app.logout()
                    }
                ]
            },
            this.organizationsMenuItem
        ];

        await this.setup();
    }

    private async setup() {
        if (!this.userService.isLogged) {
            return;
        }

        try {
            let linkedOrgsInfo = await this.accountService.getLinkedOrgsInfo();
            let currentUser = linkedOrgsInfo.userLogins.find(ul => ul.id === this.userService.currentUser.id);

            const curOrg = currentUser?.organizations.find(org => org.isCurrentTenant) ?? currentUser?.organizations[0];
            this.currentOrganization = { id: curOrg.id, name: curOrg.name };

            this.buildOrganizationItems(linkedOrgsInfo.userLogins);
        } catch {}
    }

    private buildOrganizationItems(childrenInfo: UserLogins[]) {
        this.organizationsMenuItem.items = [];
        for (const ul of childrenInfo) {
            for (const org of ul.organizations) {
                this.organizationsMenuItem.items.push({
                    label: this.getMenuItemLabel(ul, org),
                    id: `${ul.id};${org.id}`,
                    icon: org.id_original === PassportOrganizationId && "pi pi-book",
                    command: () => this.switch(ul.id, org),
                    escape: false,
                    disabled:
                        this.userService.currentUser.id === ul.id &&
                        this.currentOrganization.id === org.id &&
                        !this.appOnlineStateService.isAppOnline
                });
            }
        }
    }

    private switch(loginId: string, org: Organization) {
        const switchOperation = org.id_original === PassportOrganizationId
            ? this.linkedUserService.switchPatientPassport()
            : this.linkedUserService.switchUser(loginId, org.id);
    }

    private getMenuItemLabel(ul: UserLogins, org: Organization) {
        let label = `<b>${org.name}</b>`;
        if (org.id_original === PassportOrganizationId) {
            return label;
        }

        return label + "<br/>" + ul.email;
    }
}
